import React, { useEffect, useState } from 'react';
import "../css/ProfilePage.css";
import defaultProfilePic from '../assets/image/defaultpic.jpeg';
import { updateProfile, fetchUserProfile, uploadProfileImage } from '../api/userAPI';
import { useUser } from '../contexts/UserContext';

const initialProfile = {
  fullName: '',
  about: '',
  collegeName: '',
  linkedIn: '',
  gitHub: '',
  photoUrl: '',
};

function ProfilePage() {
  const { user, setUser } = useUser();
  const [profile, setProfile] = useState(initialProfile);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const syncProfileState = (nextUserProfile) => {
    setProfile({
      ...initialProfile,
      ...nextUserProfile,
    });

    const nextUser = {
      ...user,
      ...nextUserProfile,
    };
    setUser(nextUser);
    localStorage.setItem('user', JSON.stringify(nextUser));
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user?.username) {
        setLoading(false);
        return;
      }

      try {
        const profileData = await fetchUserProfile(user.username);
        setProfile({
          ...initialProfile,
          ...profileData.user,
        });
      } catch (profileError) {
        console.error('Error fetching profile data:', profileError);
        setError(profileError.message || 'Unable to load profile right now.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setProfile((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    try {
      const updatedProfile = await updateProfile(profile);
      syncProfileState(updatedProfile.user);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (submitError) {
      console.error('Error updating profile:', submitError);
      setError(submitError.message || 'Profile update failed.');
      setSuccess(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setError('');
    setSuccess(false);

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Please upload an image smaller than 5 MB.');
      return;
    }

    setUploadingImage(true);

    try {
      const uploadResult = await uploadProfileImage(file);
      const updatedProfile = await updateProfile({ photoUrl: uploadResult.secure_url });
      syncProfileState(updatedProfile.user);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (uploadError) {
      console.error('Image upload failed:', uploadError);
      setError(uploadError.message || 'Failed to upload image.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  if (loading) {
    return <div className="profile-page profile-state">Loading profile...</div>;
  }

  return (
    <div className="profile-page">
      <div className="profile-page-header">
        <img src={profile.photoUrl || defaultProfilePic} alt="Profile" className="profile-avatar" />
        <div>
          <span className="profile-eyebrow">Public profile</span>
          <h1>{profile.fullName || user?.username || 'Your profile'}</h1>
          <p>Keep your public page current so readers can trust the voice behind your interview reports.</p>
          <div className="profile-identity-row">
            <span>@{user?.username}</span>
            <span>{user?.authProvider === 'google' ? 'Google account' : 'Email account'}</span>
          </div>
          <div className="profile-upload-row">
            <label className="profile-upload-button" htmlFor="profileImageUpload">
              {uploadingImage ? 'Uploading...' : 'Upload profile image'}
            </label>
            <input
              id="profileImageUpload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={uploadingImage}
              className="profile-file-input"
            />
            <span className="profile-upload-note">PNG, JPG, WEBP up to 5 MB.</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        {error && <div className="profile-alert error">{error}</div>}
        {success && <div className="profile-alert success">Profile saved successfully.</div>}

        <div className="profile-grid">
          <div className="form-group profileform">
            <label htmlFor="fullName">Full name</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={profile.fullName || ''}
              onChange={handleChange}
            />
          </div>

          <div className="form-group profileform">
            <label htmlFor="collegeName">College</label>
            <input
              type="text"
              id="collegeName"
              name="collegeName"
              value={profile.collegeName || ''}
              onChange={handleChange}
            />
          </div>

          <div className="form-group profileform full-width">
            <label htmlFor="about">About</label>
            <textarea
              id="about"
              name="about"
              value={profile.about || ''}
              onChange={handleChange}
              rows="4"
            />
          </div>

          <div className="form-group profileform">
            <label htmlFor="linkedIn">LinkedIn URL</label>
            <input
              type="url"
              id="linkedIn"
              name="linkedIn"
              value={profile.linkedIn || ''}
              onChange={handleChange}
            />
          </div>

          <div className="form-group profileform">
            <label htmlFor="gitHub">GitHub URL</label>
            <input
              type="url"
              id="gitHub"
              name="gitHub"
              value={profile.gitHub || ''}
              onChange={handleChange}
            />
          </div>

          <div className="form-group profileform full-width">
            <label htmlFor="photoUrl">Photo URL</label>
            <input
              type="url"
              id="photoUrl"
              name="photoUrl"
              value={profile.photoUrl || ''}
              onChange={handleChange}
              placeholder="https://..."
            />
          </div>
        </div>

        <button className="profileButton" type="submit">Save profile</button>
      </form>
    </div>
  );
}

export default ProfilePage;
